import { PDFDocument, StandardFonts, PageSizes } from "pdf-lib";
import {
  MONTH_NAMES_ID, DAY_NAMES_ID, daysInMonth, dayOfWeek,
  toISO, formatLong,
} from "@/lib/calendar/dates";
import {
  getHolidaysForYear, getHolidaysForMonth,
} from "@/data/holidays";
import { getHariPentingForMonth, HARI_PENTING } from "@/data/hari-penting";
import { getDayInfo } from "@/lib/calendar/info";
import { gregorianToHijri, formatHijri } from "@/lib/calendar/hijri";
import { getHijriEvent } from "@/lib/calendar/hijri-events";
import { gregorianToJawa } from "@/lib/calendar/jawa";
import {
  RED, BLUE, GRAY, TEXT, LIGHT, GREEN,
  type BaseOpts, embedLogo, drawBrandHeader, wrapText, drawFooter, drawLegendList,
  DAY_NAMES_SHORT_ID_PDF,
} from "./pdf-shared";

export { downloadPDF } from "./pdf-shared";

function dayLabel(year: number, month: number, day: number): string {
  return DAY_NAMES_ID[dayOfWeek(year, month, day)];
}

/** Per-month grid PDF (A4 portrait) */
export async function generateMonthPDF(year: number, month: number, opts: BaseOpts = {}): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage(PageSizes.A4);
  const { width: W, height: H } = { width: page.getWidth(), height: page.getHeight() };
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedLogo(doc, opts.logo, opts.useDefaultLogo);

  // Brand header (mark + hari.co.id wordmark) — title goes on the next line.
  const brandBottom = drawBrandHeader(page, logo, font, bold, 30, H - 22);
  page.drawText(`${MONTH_NAMES_ID[month - 1]} ${year}`, {
    x: 30, y: brandBottom - 24, size: 22, font: bold, color: TEXT,
  });
  const firstHijri = gregorianToHijri(year, month, 1);
  const firstJawa = gregorianToJawa(year, month, 1);
  page.drawText(
    `${formatHijri(firstHijri)}  /  ${firstJawa.bulanJawa} ${firstJawa.tahunJawa} ${firstJawa.windu}`,
    { x: 30, y: brandBottom - 40, size: 9, font, color: GRAY }
  );

  // Grid (start below the title block)
  const top = brandBottom - 56;
  const gridLeft = 30;
  const gridRight = W - 30;
  const cellW = (gridRight - gridLeft) / 7;
  const headerH = 22;

  // Reserve room for holiday list (~200pt) and footer (~80pt).
  const reservedBottom = 80 + 220;
  const total = daysInMonth(year, month);
  const firstDow = dayOfWeek(year, month, 1);
  const cells = firstDow + total;
  const rows = Math.ceil(cells / 7);
  const cellH = Math.max(56, Math.min(82, (top - headerH - reservedBottom) / rows));

  // Day headers
  for (let i = 0; i < 7; i++) {
    const x = gridLeft + i * cellW;
    page.drawRectangle({ x, y: top - headerH, width: cellW, height: headerH, color: LIGHT });
    page.drawText(DAY_NAMES_SHORT_ID_PDF[i].toUpperCase(), {
      x: x + cellW / 2 - 8, y: top - headerH + 7, size: 9, font: bold,
      color: i === 0 ? RED : TEXT,
    });
  }

  const holidays = getHolidaysForMonth(year, month);
  const hariPenting = getHariPentingForMonth(month);

  for (let i = 0; i < rows * 7; i++) {
    const col = i % 7;
    const row = Math.floor(i / 7);
    const x = gridLeft + col * cellW;
    const y = top - headerH - (row + 1) * cellH;
    page.drawRectangle({ x, y, width: cellW, height: cellH, borderWidth: 0.5, borderColor: LIGHT });
    const dayNum = i - firstDow + 1;
    if (dayNum < 1 || dayNum > total) continue;
    const iso = toISO(year, month, dayNum);
    const isSunday = col === 0;
    const dayHolidays = holidays.filter((h) => h.date === iso);
    const isLibur = dayHolidays.some((h) => h.type === "libur");
    const isCuti = dayHolidays.some((h) => h.type === "cuti-bersama");
    const info = getDayInfo(year, month, dayNum);
    const hijriEv = getHijriEvent(info.hijri.month, info.hijri.day);

    page.drawText(String(dayNum), {
      x: x + 4, y: y + cellH - 14, size: 13, font: bold,
      color: isSunday || isLibur ? RED : TEXT,
    });
    // Hijri & pasaran small
    page.drawText(`${info.hijri.day}`, {
      x: x + cellW - 14, y: y + cellH - 12, size: 7, font, color: GRAY,
    });
    page.drawText(info.pasaran, { x: x + 4, y: y + cellH - 26, size: 6, font, color: GRAY });

    // Holiday / event name (wrapped, up to 3 lines)
    const labelText = dayHolidays[0]?.name
      ?? hariPenting.find((h) => {
        const [, dd] = h.monthDay.split("-").map(Number);
        return dd === dayNum;
      })?.name
      ?? hijriEv?.name;
    const labelColor = isLibur ? RED : isCuti ? BLUE : hijriEv && !dayHolidays.length ? GREEN : GRAY;
    if (labelText) {
      const lines = wrapText(labelText, font, 6, cellW - 8).slice(0, 3);
      let ly = y + 6 + (lines.length - 1) * 7;
      for (const ln of lines) {
        page.drawText(ln, { x: x + 4, y: ly, size: 6, font, color: labelColor });
        ly -= 7;
      }
    }
  }

  // Daftar lengkap libur, cuti, dan hari penting (2 kolom)
  const listTop = top - headerH - rows * cellH - 16;
  const colW = (W - 60 - 16) / 2;
  const liburItems = holidays.filter((h) => h.type === "libur").map((h) => {
    const p = h.date.split("-").map(Number);
    return { label: `${dayLabel(p[0], p[1], p[2])}, ${p[2]} ${MONTH_NAMES_ID[p[1] - 1]} - ${h.name}`, color: RED };
  });
  const cutiItems = holidays.filter((h) => h.type === "cuti-bersama").map((h) => {
    const p = h.date.split("-").map(Number);
    return { label: `${dayLabel(p[0], p[1], p[2])}, ${p[2]} ${MONTH_NAMES_ID[p[1] - 1]} - ${h.name}`, color: BLUE };
  });
  const hpItems = hariPenting.map((h) => {
    const [, dd] = h.monthDay.split("-").map(Number);
    return { label: `${dayLabel(year, month, dd)}, ${dd} ${MONTH_NAMES_ID[month - 1]} - ${h.name}`, color: GRAY };
  });

  // Hari penting Islam yang jatuh di bulan Masehi ini
  const islamicItems: Array<{ label: string; color: typeof GREEN }> = [];
  for (let d = 1; d <= total; d++) {
    const h = gregorianToHijri(year, month, d);
    const ev = getHijriEvent(h.month, h.day);
    if (ev) {
      islamicItems.push({
        label: `${dayLabel(year, month, d)}, ${d} ${MONTH_NAMES_ID[month - 1]} - ${ev.name} (${ev.hDay} ${formatHijri({ year: h.year, month: h.month, day: h.day }).split(" ").slice(1).join(" ")})`,
        color: GREEN,
      });
    }
  }

  // Kolom kiri: Libur + Cuti
  let yL = drawLegendList(page, font, bold, [...liburItems, ...cutiItems], 30, listTop, colW, "Libur Nasional & Cuti Bersama");
  if (liburItems.length === 0 && cutiItems.length === 0) {
    page.drawText("Tidak ada libur nasional/cuti bersama di bulan ini.", { x: 30, y: yL, size: 8, font, color: GRAY });
  }
  // Kolom kanan: Hari Penting + Islam
  drawLegendList(page, font, bold, [...islamicItems, ...hpItems], 30 + colW + 16, listTop, colW, "Hari Penting & Islam");

  drawFooter(page, font, opts.siteUrl ?? `/${month}-${year}`);
  return doc.save();
}

/** Year PDF (A4 landscape, 12 mini grids) */
export async function generateYearPDF(year: number, opts: BaseOpts = {}): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage([PageSizes.A4[1], PageSizes.A4[0]]); // landscape
  const W = page.getWidth(), H = page.getHeight();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedLogo(doc, opts.logo, opts.useDefaultLogo);

  const brandBottom = drawBrandHeader(page, logo, font, bold, 30, H - 18, "Kalender Indonesia");
  page.drawText(`Kalender ${year}`, { x: 30, y: brandBottom - 22, size: 18, font: bold, color: TEXT });
  page.drawText("Masehi, Hijriah & Jawa", { x: W - 200, y: brandBottom - 22, size: 9, font, color: GRAY });

  const cols = 4, rows = 3;
  const padX = 30, padY = (H - brandBottom) + 38, gap = 10;
  const monthW = (W - padX * 2 - gap * (cols - 1)) / cols;
  const monthH = (H - padY - 60 - gap * (rows - 1)) / rows;
  const holidays = getHolidaysForYear(year);

  for (let m = 1; m <= 12; m++) {
    const idx = m - 1;
    const cx = padX + (idx % cols) * (monthW + gap);
    const cy = H - padY - Math.floor(idx / cols) * (monthH + gap);
    page.drawRectangle({ x: cx, y: cy - monthH, width: monthW, height: monthH, borderWidth: 0.5, borderColor: LIGHT });
    page.drawText(`${MONTH_NAMES_ID[m - 1]}`, { x: cx + 6, y: cy - 14, size: 9, font: bold, color: TEXT });

    const cellW = (monthW - 8) / 7;
    // reserve ~36pt at the bottom for 3 holiday lines
    const cellH = (monthH - 30 - 36) / 7;
    // Day headers
    for (let i = 0; i < 7; i++) {
      page.drawText(DAY_NAMES_SHORT_ID_PDF[i][0], {
        x: cx + 4 + i * cellW + cellW / 2 - 2, y: cy - 28, size: 6, font: bold,
        color: i === 0 ? RED : GRAY,
      });
    }
    const total = daysInMonth(year, m);
    const first = dayOfWeek(year, m, 1);
    for (let d = 1; d <= total; d++) {
      const pos = first + d - 1;
      const col = pos % 7;
      const row = Math.floor(pos / 7);
      const x = cx + 4 + col * cellW + cellW / 2 - 4;
      const y = cy - 30 - (row + 1) * cellH + 2;
      const iso = toISO(year, m, d);
      const isLibur = holidays.some((h) => h.date === iso && h.type === "libur");
      page.drawText(String(d), {
        x, y, size: 6, font,
        color: col === 0 || isLibur ? RED : TEXT,
      });
    }
    // Holiday names at the bottom of each mini month — wrap text, no truncation.
    const monthHolidays = holidays.filter((h) => h.date.startsWith(`${year}-${String(m).padStart(2, "0")}`));
    const bottomLimitY = cy - monthH + 6;
    let hy = cy - monthH + 30;
    for (const h of monthHolidays) {
      if (hy < bottomLimitY) break;
      const day = h.date.slice(8, 10);
      const text = `${day} ${h.name}`;
      const lines = wrapText(text, font, 5, monthW - 10);
      for (const ln of lines) {
        if (hy < bottomLimitY) break;
        page.drawText(ln, {
          x: cx + 4, y: hy, size: 5, font,
          color: h.type === "libur" ? RED : BLUE,
        });
        hy -= 6.2;
      }
    }
  }

  drawFooter(page, font, opts.siteUrl ?? `/${year}`);

  // Halaman 2 portrait: daftar lengkap setahun
  const p2 = doc.addPage(PageSizes.A4);
  const W2 = p2.getWidth(), H2 = p2.getHeight();
  const brand2 = drawBrandHeader(p2, logo, font, bold, 30, H2 - 22);
  p2.drawText(`Daftar Libur Nasional & Cuti Bersama ${year}`, { x: 30, y: brand2 - 20, size: 16, font: bold, color: TEXT });
  const liburItems = holidays.filter(h => h.type === "libur").map(h => {
    const p = h.date.split("-").map(Number);
    return { label: `${dayLabel(p[0], p[1], p[2])}, ${p[2]} ${MONTH_NAMES_ID[p[1] - 1]} ${p[0]} - ${h.name}`, color: RED };
  });
  const cutiItems = holidays.filter(h => h.type === "cuti-bersama").map(h => {
    const p = h.date.split("-").map(Number);
    return { label: `${dayLabel(p[0], p[1], p[2])}, ${p[2]} ${MONTH_NAMES_ID[p[1] - 1]} ${p[0]} - ${h.name}`, color: BLUE };
  });
  const hpAll = HARI_PENTING.map(h => {
    const [mm, dd] = h.monthDay.split("-").map(Number);
    return { label: `${dd} ${MONTH_NAMES_ID[mm - 1]} - ${h.name}`, color: GRAY };
  });
  const colW2 = (W2 - 60 - 16) / 2;
  const listTopY2 = brand2 - 50;
  drawLegendList(p2, font, bold, liburItems, 30, listTopY2, colW2, `Libur Nasional (${liburItems.length})`);
  let yR = drawLegendList(p2, font, bold, cutiItems, 30 + colW2 + 16, listTopY2, colW2, `Cuti Bersama (${cutiItems.length})`);
  yR -= 12;
  drawLegendList(p2, font, bold, hpAll, 30 + colW2 + 16, yR, colW2, "Hari Penting Tahunan");

  // Hari Besar Islam: scan setiap tanggal di tahun ini.
  const islamYearItems: Array<{ label: string; color: typeof GREEN }> = [];
  for (let mm = 1; mm <= 12; mm++) {
    const dim = daysInMonth(year, mm);
    for (let dd = 1; dd <= dim; dd++) {
      const h = gregorianToHijri(year, mm, dd);
      const ev = getHijriEvent(h.month, h.day);
      if (ev) {
        islamYearItems.push({
          label: `${dayLabel(year, mm, dd)}, ${dd} ${MONTH_NAMES_ID[mm - 1]} ${year} - ${ev.name} (${h.day} ${formatHijri(h).split(" ").slice(1).join(" ")})`,
          color: GREEN,
        });
      }
    }
  }
  // Place Islamic events in the left column below libur list.
  // Compute Y where libur ends — re-draw below it. Use a simple offset since libur list grows downward.
  // For safety, add a third page if many events.
  const pIslam = doc.addPage(PageSizes.A4);
  const Hi = pIslam.getHeight();
  const brandI = drawBrandHeader(pIslam, logo, font, bold, 30, Hi - 22);
  pIslam.drawText(`Hari Besar Islam ${year}`, { x: 30, y: brandI - 20, size: 16, font: bold, color: TEXT });
  pIslam.drawText(
    "Tanggal Hijriah dapat berbeda 1 hari dari rukyatul hilal. Penetapan resmi mengikuti sidang isbat Kemenag.",
    { x: 30, y: brandI - 36, size: 8, font, color: GRAY, maxWidth: pIslam.getWidth() - 60 },
  );
  drawLegendList(pIslam, font, bold, islamYearItems, 30, brandI - 60, pIslam.getWidth() - 60, `Daftar (${islamYearItems.length})`);
  drawFooter(pIslam, font, opts.siteUrl ?? `/${year}`);

  drawFooter(p2, font, opts.siteUrl ?? `/${year}`);

  return doc.save();
}

/** Single date detail PDF */
export async function generateDayPDF(year: number, month: number, day: number, opts: BaseOpts = {}): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage(PageSizes.A4);
  const W = page.getWidth(), H = page.getHeight();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logo = await embedLogo(doc, opts.logo, opts.useDefaultLogo);

  const info = getDayInfo(year, month, day);
  const brandBottom = drawBrandHeader(page, logo, font, bold, 30, H - 22);
  page.drawText(`${info.dayName}, ${formatLong(year, month, day)}`, {
    x: 30, y: brandBottom - 24, size: 20, font: bold,
    color: info.isTanggalMerah ? RED : TEXT,
  });

  let y = brandBottom - 60;
  const line = (label: string, val: string) => {
    page.drawText(label, { x: 30, y, size: 9, font, color: GRAY });
    page.drawText(val, { x: 170, y, size: 10, font: bold, color: TEXT, maxWidth: W - 200 });
    y -= 18;
  };

  page.drawText("Penanggalan", { x: 30, y, size: 12, font: bold, color: TEXT }); y -= 18;
  line("Masehi", `${day} ${MONTH_NAMES_ID[month - 1]} ${year}`);
  line("Hijriah", info.hijriFormatted);
  line("Jawa", `${info.jawa.tanggalJawa} ${info.jawa.bulanJawa} ${info.jawa.tahunJawa} ${info.jawa.windu}`);
  line("Hari", info.dayName);
  line("Pasaran", info.jawa.pasaran);
  line("Weton", info.jawa.weton);
  line("Neptu", `${info.jawa.neptu} (hari ${info.jawa.neptuHari} + pasaran ${info.jawa.neptuPasaran})`);
  line("Pancasuda", info.jawa.pancasuda);
  line("Wuku", info.jawa.wuku);
  line("Pranata Mangsa", info.jawa.pranataMangsa.name);
  line("Bali", `${info.bali.saptawara} ${info.bali.pancawara}, Wuku ${info.bali.wuku}`);
  line("Tionghoa", `${info.china.lunarDay} ${info.china.monthName} ${info.china.lunarYear}`);
  line("Shio", `${info.china.zodiac} (${info.china.element} ${info.china.yinYang}) - ${info.china.cyclicalYear}`);
  if (info.china.festivals.length > 0) {
    line("Festival Tionghoa", info.china.festivals.join(", "));
  }

  y -= 8;
  page.drawText("Libur & Hari Penting", { x: 30, y, size: 12, font: bold, color: TEXT }); y -= 18;
  const islamEv = getHijriEvent(info.hijri.month, info.hijri.day);
  if (info.holidays.length === 0 && info.hariPenting.length === 0 && !islamEv) {
    page.drawText("Tidak ada libur atau hari penting pada tanggal ini.", { x: 30, y, size: 9, font, color: GRAY });
    y -= 14;
  }
  for (const h of info.holidays) {
    const isLibur = h.type === "libur";
    const tag = isLibur ? "Libur Nasional" : "Cuti Bersama";
    page.drawText(`• [${tag}] ${h.name}`, { x: 30, y, size: 10, font, color: isLibur ? RED : BLUE });
    y -= 14;
  }
  if (islamEv) {
    page.drawText(`• [Hari Besar Islam] ${islamEv.name}`, { x: 30, y, size: 10, font, color: GREEN });
    y -= 12;
    const descLines = wrapText(islamEv.descId, font, 8, W - 80);
    for (const ln of descLines) {
      page.drawText(ln, { x: 42, y, size: 8, font, color: GRAY });
      y -= 10;
    }
    y -= 4;
  }
  for (const h of info.hariPenting) {
    page.drawText(`• [Hari Penting] ${h.name}`, { x: 30, y, size: 10, font, color: GRAY });
    y -= 14;
  }

  drawFooter(page, font, opts.siteUrl ?? info.pathDate);
  return doc.save();
}